import { env } from "@/config/env";

import { ApiError } from "./ApiError";
import { extractBodyMessage } from "./extractBodyMessage";

type RequestBody = Record<string, unknown> | unknown[] | undefined;

interface RequestOptions extends Omit<
  RequestInit,
  "body" | "method" | "signal"
> {
  body?: RequestBody;
  timeoutMs?: number;
}

// 0 makes getHttpErrorClass return "unknown", so Sentry logs it at error level.
const TIMEOUT_STATUS = 0;

const parseBody = async (response: Response): Promise<unknown> => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

const isTimeout = (error: unknown, timeoutSignal?: AbortSignal): boolean =>
  Boolean(timeoutSignal?.aborted) && error instanceof DOMException;

const request = async <T>(
  method: string,
  path: string,
  options: RequestOptions = {},
): Promise<T> => {
  const { body, headers, timeoutMs, ...init } = options;
  const url = `${env.VITE_BIFROST_URL.replace(/\/$/, "")}${path}`;

  const timeoutSignal =
    timeoutMs === undefined ? undefined : AbortSignal.timeout(timeoutMs);

  let response: Response;
  let responseBody: unknown;
  try {
    response = await fetch(url, {
      ...init,
      method,
      signal: timeoutSignal,
      headers: {
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
        ...headers,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    responseBody = await parseBody(response);
  } catch (error) {
    if (isTimeout(error, timeoutSignal)) {
      throw new ApiError({
        status: TIMEOUT_STATUS,
        endpoint: path,
        responseBody: null,
        message: `Request timed out: ${path}`,
      });
    }

    throw error;
  }

  if (!response.ok) {
    throw new ApiError({
      status: response.status,
      endpoint: path,
      responseBody,
      message:
        extractBodyMessage(responseBody) ??
        `API Error: ${path} → ${response.status}`,
    });
  }

  return responseBody as T;
};

export const apiClient = {
  get: <T = unknown>(path: string, options?: RequestOptions) =>
    request<T>("GET", path, options),
  post: <T = unknown>(
    path: string,
    body?: RequestBody,
    options?: RequestOptions,
  ) => request<T>("POST", path, { ...options, body }),
  patch: <T = unknown>(
    path: string,
    body?: RequestBody,
    options?: RequestOptions,
  ) => request<T>("PATCH", path, { ...options, body }),
  put: <T = unknown>(
    path: string,
    body?: RequestBody,
    options?: RequestOptions,
  ) => request<T>("PUT", path, { ...options, body }),
  delete: <T = unknown>(path: string, options?: RequestOptions) =>
    request<T>("DELETE", path, options),
};
